/**
 * UIMessage → ModelMessage[] rendering pipeline.
 *
 * Takes the chat thread's `AtlasMessage[]` (alias for `UIMessage<...>[]`)
 * and produces the prose-only `ModelMessage[]` we send to the LLM. Tool
 * call/result pairs are already paired on a single `ToolUIPart` /
 * `DynamicToolUIPart` keyed by `toolCallId`, so we render each part
 * directly instead of round-tripping through `convertToModelMessages` and
 * losing the pairing.
 *
 * Tool-state dispatch is a flat record lookup keyed by
 * `ToolUIPart['state']`. Each leaf renderer returns JSX rendered through
 * `renderInstructions` — no string concatenation, no inline XML, no
 * cross-part state.
 */

import {
  type DynamicToolUIPart,
  getToolName,
  isReasoningUIPart,
  isTextUIPart,
  isToolUIPart,
  type ModelMessage,
  type ToolUIPart,
} from 'ai';
import type { FC, ReactNode } from 'react';

import type { AtlasMessage } from './types.ts';
import { renderInstructions } from '#plugin-chat/lib/instructions/reconciler.ts';
import { DefaultToolCall, DefaultToolDenied, DefaultToolError } from '#plugin-chat/lib/tool/message-components.tsx';
import type { ErasedToolMessageFormatters } from '#plugin-chat/lib/tool/message-formatters.ts';
import type { ToolOutputError } from '#plugin-chat/lib/tool/output-types.ts';
import { parseResultEnvelope } from '#plugin-chat/lib/tool/result-envelope.ts';

/**
 * The slice of the assistant-ui model-context tool entry the transcript
 * builder reads: just the per-tool message formatters. Structural by
 * design so the renderer doesn't hard-depend on the tool-registry type.
 */
export type ToolWithMessage = {
  message?: ErasedToolMessageFormatters;
};

export type RenderDeps = {
  toolRegistry: Record<string, ToolWithMessage>;
};

type AnyToolUiPart = ToolUIPart | DynamicToolUIPart;

const toolArgs = (part: AnyToolUiPart): Record<string, unknown> => (part.input ?? {}) as Record<string, unknown>;

const isBlank = (text: string): boolean => text.trim().length === 0;

type ToolStateRenderer<S extends AnyToolUiPart['state']> = FC<{
  part: Extract<AnyToolUiPart, { state: S }>;
  deps: RenderDeps;
}>;

function ToolCallSection({ part, deps }: { part: AnyToolUiPart; deps: RenderDeps }) {
  const tool = getToolName(part);
  const args = toolArgs(part);
  const node = deps.toolRegistry[tool]?.message?.call?.({ args, toolName: tool });
  // `null`/`undefined` ⇒ no formatter, or the tool opted out. Fall back
  // to the canonical TOON args block so the call still appears in the
  // transcript rather than silently vanishing.
  return node == null ? <DefaultToolCall args={args} toolName={tool} /> : node;
}

function ToolProtocolErrorSection({
  part,
  deps,
}: {
  part: Extract<AnyToolUiPart, { state: 'output-error' }>;
  deps: RenderDeps;
}) {
  // Protocol failures (the AI SDK marked the call errored) carry only
  // free text. Synthesize a structured envelope so they flow through the
  // same per-tool `message.error` formatter as tool-level failures.
  const envelope: ToolOutputError = {
    success: false,
    errorType: 'ProtocolError',
    error: part.errorText ?? 'Tool execution failed.',
  };
  return <ToolErrorSection deps={deps} envelope={envelope} part={part} />;
}

function ToolDeniedSection({
  part,
  deps,
}: {
  part: Extract<AnyToolUiPart, { state: 'output-denied' }>;
  deps: RenderDeps;
}) {
  const tool = getToolName(part);
  const args = toolArgs(part);
  const reason = part.approval.reason ?? 'Execution denied';
  const node = deps.toolRegistry[tool]?.message?.denied?.({ args, reason, toolName: tool });
  return node == null ? <DefaultToolDenied args={args} reason={reason} toolName={tool} /> : node;
}

function ToolCompletedFallback({ toolName }: { toolName: string }) {
  return (
    <p>
      Tool <code>{toolName}</code> completed.
    </p>
  );
}

function ToolSuccessSection({
  part,
  deps,
}: {
  part: Extract<AnyToolUiPart, { state: 'output-available' }>;
  deps: RenderDeps;
}) {
  const tool = getToolName(part);
  const args = toolArgs(part);
  const formatter = deps.toolRegistry[tool]?.message?.result;
  if (!formatter) {
    return <ToolCompletedFallback toolName={tool} />;
  }
  let node: ReturnType<typeof formatter>;
  try {
    node = formatter({ args, output: part.output });
  } catch (e) {
    // A success-classified payload that the formatter can't read (e.g. a
    // mis-shaped envelope) must NOT collapse to a blank node the
    // transcript filter would silently drop — degrade visibly so the LLM
    // still sees the tool resolved.
    console.error(`[render-pipeline] result formatter threw for "${tool}" — degrading to completed fallback`, e);
    return <ToolCompletedFallback toolName={tool} />;
  }
  // Formatter explicitly silenced the result: fall back to canonical
  // "completed" prose so the empty-string filter doesn't drop the whole
  // tool result without a trace in the transcript.
  return node == null ? <ToolCompletedFallback toolName={tool} /> : node;
}

function ToolResultSection({
  part,
  deps,
}: {
  part: Extract<AnyToolUiPart, { state: 'output-available' }>;
  deps: RenderDeps;
}) {
  // Discriminate success vs. structured failure via the single source of
  // truth (`parseResultEnvelope`), shared with `useRerunTool` and
  // `ToolStatus`. `<Tool>` success envelopes carry no `success` field;
  // only genuine failures carry the `success: false` marker.
  const result = parseResultEnvelope(part.output);
  if (result.kind === 'success') {
    return <ToolSuccessSection deps={deps} part={part} />;
  }
  return <ToolErrorSection deps={deps} envelope={result.error} part={part} />;
}

/**
 * Renders a failed tool call. Both tool-level structured failures
 * (`output-available` + `success: false`) and protocol errors
 * (`output-error`) route here through a uniform {@link ToolOutputError}
 * envelope, so a tool's `message.error` formatter covers every failure
 * mode. Falls back to `DefaultToolError` when none is declared.
 */
function ToolErrorSection({
  part,
  deps,
  envelope,
}: {
  part: AnyToolUiPart;
  deps: RenderDeps;
  envelope: ToolOutputError;
}) {
  const tool = getToolName(part);
  const args = toolArgs(part);
  const node = deps.toolRegistry[tool]?.message?.error?.({ args, envelope, toolName: tool });
  return node == null ? <DefaultToolError args={args} envelope={envelope} toolName={tool} /> : node;
}

/**
 * Dispatch table for each `ToolUIPart` lifecycle state. `null` entries
 * mean "do not emit a transcript message" (in-flight or pre-execution
 * states have no settled prose yet).
 */
const TOOL_STATE_RENDERERS = {
  'input-streaming': null,
  'input-available': ToolCallSection,
  'approval-requested': null,
  'approval-responded': null,
  'output-available': ToolResultSection,
  'output-error': ToolProtocolErrorSection,
  'output-denied': ToolDeniedSection,
} satisfies { [S in AnyToolUiPart['state']]: ToolStateRenderer<S> | null };

export function SystemReminder() {
  return (
    <p>
      <p>SYSTEM REMINDER:</p>
      <p>
        If you need additional data to complete the request, for example entity names or aggregates, or fetching
        additional datasets: Just fetch that data now. Do not wait for the user to ask for it.
      </p>
      {/* <p> */}
      {/*   If further context is needed to provide the user with a precise answer: Do not wait for the user to ask; eagerly */}
      {/*   prepare additional datasets using {QueryKnowledgeGraphTool.mention}/{TransformDatasetTool.mention} before */}
      {/*   answering the user with precise, up-to-date information. */}
      {/* </p> */}
      {/* <p>If you've provided Use the {AskUserQuestionTool.mention} tool to ask relevant follow-up questions to the user.</p> */}
    </p>
  );
}

type RenderedNode = { role: 'user' | 'assistant' | 'system'; node: ReactNode };

const renderPart = (
  part: AtlasMessage['parts'][number],
  role: AtlasMessage['role'],
  deps: RenderDeps,
): RenderedNode[] => {
  if (isTextUIPart(part)) {
    // Skip whitespace-only text parts up front so we don't spin up a
    // reconciler container just to produce an empty string the
    // downstream filter would discard.
    if (isBlank(part.text)) {
      return [];
    }
    return [{ role, node: part.text }];
  }

  if (isReasoningUIPart(part)) {
    return [];
  }

  if (part.type === 'step-start') {
    return [];
  }

  if (isToolUIPart(part)) {
    // biome-ignore lint/suspicious/noExplicitAny: state-keyed dispatch — the table's ABI matches structurally, but the per-state union collapses to `never` at the lookup site
    const Renderer = TOOL_STATE_RENDERERS[part.state] as FC<{ part: any; deps: RenderDeps }> | null;
    if (!Renderer) {
      return [];
    }

    // Terminal tool states (output-available / output-error / output-denied)
    // arrive on a single ToolUIPart whose state has already advanced past
    // 'input-available' — so the dispatch table's call announcement never
    // fires for completed tools. Re-emit it here as a sibling system
    // message so the LLM sees both "I called X with these args" and the
    // result/error/denial that followed.
    const isTerminal =
      part.state === 'output-available' || part.state === 'output-error' || part.state === 'output-denied';

    const out: RenderedNode[] = [];
    if (isTerminal) {
      out.push({ role: 'system', node: <ToolCallSection deps={deps} part={part} /> });
    }
    out.push({ role: 'system', node: <Renderer deps={deps} part={part} /> });
    return out;
  }

  return [];
};

const toModelMessage = (rendered: RenderedNode): ModelMessage | null => {
  let text: string;
  try {
    text = renderInstructions(rendered.node);
  } catch (e) {
    // A node that throws during render must NOT vanish from the transcript:
    // degrade to a visible fallback so the agent still learns the tool
    // resolved instead of silently never seeing the message.
    console.error('[render-pipeline] failed to render node for transcript — degrading to visible fallback', e);
    return {
      role: rendered.role,
      content: '[A tool result could not be rendered — the tool completed but its output could not be formatted.]',
    };
  }
  if (isBlank(text)) {
    return null;
  }
  return { role: rendered.role, content: text };
};

/**
 * Render an `AtlasMessage[]` thread to a prose-only `ModelMessage[]`
 * transcript suitable for `streamText`.
 *
 * Tool call/result pairs are read off `ToolUIPart`/`DynamicToolUIPart`
 * directly — the SDK already pairs them on the UI side keyed by
 * `toolCallId`, so we never round-trip through `convertToModelMessages`.
 *
 * The recency cap is applied *before* rendering so we don't pay
 * reconciler cost on messages that won't ship.
 */
export function renderMessages(messages: AtlasMessage[], deps: RenderDeps, maxMessages = 100): ModelMessage[] {
  // TODO: Perf: collect messages/message parts before rendering to avoid reconciler penalty for each part rendering
  return messages
    .slice(-maxMessages)
    .flatMap((msg) => msg.parts.flatMap((part) => renderPart(part, msg.role, deps)))
    .map(toModelMessage)
    .filter((m): m is ModelMessage => m !== null);
}
